import { Field, FieldError, FieldLabel, Input, Search } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import { Patient } from "@curai/js-fhir-tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod";
import { SubmitCancelButtons } from "../../components/SubmitCancelButtons";
import { emergencyContactFormSchema } from "../schemas";

type EmergencyContactFormValues = z.infer<typeof emergencyContactFormSchema>;

const relationshipOptions = Object.entries(
  Patient.RelationshipRoleTypeLabels,
).map(([value, label]) => ({
  value,
  label,
}));

export const UpdateEmergencyContactForm = ({
  handleSubmit,
  isUpdating,
  onCancel,
  initialValues,
}: {
  handleSubmit: (values: EmergencyContactFormValues) => void;
  isUpdating: boolean;
  onCancel: () => void;
  initialValues?: Partial<EmergencyContactFormValues>;
}) => {
  const form = useForm<EmergencyContactFormValues>({
    resolver: zodResolver(emergencyContactFormSchema),
    defaultValues: initialValues,
  });

  const initialRelationshipValue =
    (initialValues?.contactRelationshipRoleType &&
      Patient.RelationshipRoleTypeLabels[
        initialValues.contactRelationshipRoleType as keyof typeof Patient.RelationshipRoleTypeLabels
      ]) ??
    "";

  return (
    <form
      onSubmit={form.handleSubmit(handleSubmit)}
      className="flex relative flex-col gap-2 px-3 py-2 w-full"
    >
      <div className="flex w-full">
        <Controller
          control={form.control}
          name="contactName"
          render={({ field, fieldState }) => (
            <Field className="w-1/2">
              <FieldLabel htmlFor="contactName">Name</FieldLabel>
              <Input {...field} id="contactName" autoFocus />
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />
        <Controller
          control={form.control}
          name="contactRelationshipRoleType"
          render={({ fieldState }) => (
            <Field className="ml-6 w-1/2">
              <FieldLabel htmlFor="contactRelationshipRoleType">
                Relationship
              </FieldLabel>
              <Search
                input={initialRelationshipValue}
                shouldFilter={true}
                options={relationshipOptions}
                onInputChange={(searchString) => {
                  if (searchString.length === 0) {
                    form.setValue("contactRelationshipRoleType", "");
                  }
                }}
                onItemSelected={(item) => {
                  form.setValue("contactRelationshipRoleType", item.value);
                }}
                placeholder="Select a relationship"
              />
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />
      </div>
      <Controller
        control={form.control}
        name="contactPhone"
        render={({ field, fieldState }) => (
          <Field className="w-1/2 pr-3">
            <FieldLabel htmlFor="contactPhone">Phone Number</FieldLabel>
            <Input {...field} id="contactPhone" />
            <FieldError errors={[fieldState.error]} />
          </Field>
        )}
      />
      <div className="flex flex-col w-full">
        <SubmitCancelButtons
          isSaving={isUpdating}
          onCancel={() => {
            trackEvent(TRACKERS.CANCEL_EMERGENCY_CONTACT_NAME);
            onCancel();
          }}
          onSave={() => {
            trackEvent(TRACKERS.SAVE_EMERGENCY_CONTACT_NAME);
          }}
        />
      </div>
    </form>
  );
};
